import React from 'react';

const textClasses = 'text-lg text-gray-700 leading-relaxed';

const renderInline = (node, key) => {
  if (typeof node === 'string') return node;
  if (!node) return null;

  if (node.type === 'link' && node.url) {
    return (
      <a
        key={key}
        href={node.url}
        className="text-blue-700 underline underline-offset-4 hover:text-blue-800"
        rel="noopener noreferrer"
        target={node.url.startsWith('http') ? '_blank' : undefined}
      >
        {node.children?.map((child, index) => renderInline(child, `${key}-${index}`))}
      </a>
    );
  }

  let content = node.text ?? node.children?.map((child, index) => renderInline(child, `${key}-${index}`));

  if (node.code) {
    content = (
      <code key={key} className="rounded bg-gray-100 px-1.5 py-0.5 text-base text-gray-900">
        {content}
      </code>
    );
  }
  if (node.bold) content = <strong key={key}>{content}</strong>;
  if (node.italic) content = <em key={key}>{content}</em>;
  if (node.underline) content = <span className="underline">{content}</span>;

  return content;
};

const renderChildren = (children, key) =>
  children?.map((child, index) => renderInline(child, `${key}-${index}`));

const renderListItem = (item, key) => (
  <li key={key} className="pl-1">
    {renderChildren(item.children, key)}
  </li>
);

const renderBlock = (block, index) => {
  if (typeof block === 'string') {
    return (
      <p key={index} className={textClasses}>
        {block}
      </p>
    );
  }

  switch (block?.type) {
    case 'heading': {
      const level = Math.min(Math.max(Number(block.level) || 2, 2), 4);
      const Tag = `h${level}`;
      const size = level === 2 ? 'text-3xl' : level === 3 ? 'text-2xl' : 'text-xl';

      return (
        <Tag key={index} className={`${size} font-bold text-gray-900 mt-10 mb-4`}>
          {renderChildren(block.children, index)}
        </Tag>
      );
    }
    case 'paragraph':
      return (
        <p key={index} className={textClasses}>
          {renderChildren(block.children, index)}
        </p>
      );
    case 'quote':
      return (
        <blockquote
          key={index}
          className="border-l-4 border-blue-600 bg-blue-50 px-5 py-4 text-lg italic text-blue-950"
        >
          {renderChildren(block.children, index)}
        </blockquote>
      );
    case 'list': {
      const Tag = block.format === 'ordered' ? 'ol' : 'ul';
      const listClass = block.format === 'ordered' ? 'list-decimal' : 'list-disc';

      return (
        <Tag key={index} className={`${listClass} space-y-2 pl-6 ${textClasses}`}>
          {block.children?.map((item, itemIndex) => renderListItem(item, `${index}-${itemIndex}`))}
        </Tag>
      );
    }
    case 'image':
      return block.image?.url ? (
        <figure key={index} className="my-10">
          <img
            src={block.image.url}
            alt={block.image.alternativeText || block.image.caption || ''}
            className="w-full rounded-xl object-cover shadow-sm"
          />
          {block.image.caption && (
            <figcaption className="mt-3 text-center text-sm text-gray-500">
              {block.image.caption}
            </figcaption>
          )}
        </figure>
      ) : null;
    default:
      return block?.children ? (
        <p key={index} className={textClasses}>
          {renderChildren(block.children, index)}
        </p>
      ) : null;
  }
};

const renderMarkdownLite = (content) => {
  const blocks = content
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);

  return blocks.map((block, index) => {
    if (block.startsWith('### ')) {
      return (
        <h3 key={index} className="text-2xl font-bold text-gray-900 mt-10 mb-4">
          {block.replace(/^###\s+/, '')}
        </h3>
      );
    }
    if (block.startsWith('## ')) {
      return (
        <h2 key={index} className="text-3xl font-bold text-gray-900 mt-10 mb-4">
          {block.replace(/^##\s+/, '')}
        </h2>
      );
    }
    if (block.startsWith('# ')) {
      return (
        <h2 key={index} className="text-3xl font-bold text-gray-900 mt-10 mb-4">
          {block.replace(/^#\s+/, '')}
        </h2>
      );
    }

    const lines = block.split('\n').map((line) => line.trim());
    if (lines.every((line) => line.startsWith('- '))) {
      return (
        <ul key={index} className={`list-disc space-y-2 pl-6 ${textClasses}`}>
          {lines.map((line) => (
            <li key={line}>{line.replace(/^-\s+/, '')}</li>
          ))}
        </ul>
      );
    }

    return (
      <p key={index} className={textClasses}>
        {block}
      </p>
    );
  });
};

const BlogArticleBody = ({ content }) => {
  if (!content) {
    return (
      <p className={textClasses}>
        This article is being prepared by the KCG team.
      </p>
    );
  }

  const blocks = Array.isArray(content) ? content : [content];

  return (
    <div className="space-y-6">
      {typeof content === 'string' ? renderMarkdownLite(content) : blocks.map(renderBlock)}
    </div>
  );
};

export default BlogArticleBody;
